import { useCallback } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { useConstituentStatus } from '#src/app/shared/constituents';
import type { ConstituentIdentification } from '#src/app/shared/constituents';

type Result = (identification: ConstituentIdentification | null) => void;

/**
 * Stores the constituent-identification result (see `useIdentifyConstituent`) for the combo screen to
 * read on its next page load. Written by the sign hook only when the sign succeeds, so pending
 * (grayListed / guestSignByExistingUser) and failed signs are not enrolled.
 *
 * The sign hook always writes on success (null for ineligible signs) so a later combo-screen visit in
 * the same tab cannot read a stale per-petition result from an earlier sign. Owns its own try/catch so a
 * storage failure can never block a signature.
 */
export function useSetSessionStoredConstituentStatus(): Result {
	const { errorReporter } = useUtilityContext();
	const {
		data: { petition },
	} = usePetitionDetailsPageData();
	const [, setConstituentStatus] = useConstituentStatus(petition.id);

	return useCallback(
		(identification) => {
			try {
				setConstituentStatus(identification);
			} catch (error) {
				void errorReporter.report({
					error: error instanceof Error ? error : new Error('Storing constituent status failed', { cause: error }),
					context: { feature: 'sign_form' },
					params: { petitionId: petition.id },
				});
			}
		},
		[petition.id, setConstituentStatus, errorReporter],
	);
}
